import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;
const AUTH_URL = process.env.REACT_APP_API_AUTH_URL;

export const authClient = axios.create({
  baseURL: AUTH_URL,
  withCredentials: true,
});

export const apiClient = axios.create({
  baseURL: API_URL,
  withCredentials: true,
});

function attachBearer(config) {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}

authClient.interceptors.request.use(attachBearer);
apiClient.interceptors.request.use(attachBearer);

// Refresh tokens are single-use/rotating on the backend (each /refresh call
// deletes the presented token and issues a new one). On reload, several
// components mount at once and each fires its own request; if the access
// token had expired, each one 401s independently and — without this — each
// would call /refresh on its own. Only the first actually succeeds; the
// rest present the now-already-rotated token and get a second 401, whose
// catch block wipes `token`/`userId` from localStorage even though the
// session is perfectly valid. Sharing one in-flight promise means a whole
// wave of concurrent 401s triggers exactly one /refresh call.
let refreshPromise = null;

function refreshOnce() {
  if (!refreshPromise) {
    refreshPromise = authClient
      .get("/refresh")
      .then((refreshRes) => {
        const accessToken = refreshRes?.data?.accessToken;
        const userId = refreshRes?.data?.user?.id;
        if (accessToken) localStorage.setItem("token", accessToken);
        // The interceptor used to only persist the access token — on a
        // successful refresh the user id was never (re)written, so once it
        // was missing (e.g. cleared by an earlier failed/raced refresh)
        // nothing ever restored it, even though the session kept working.
        // Pages that gate their initial data load on localStorage "userId"
        // (every page's server dropdown) would then hang on "Загрузка…"
        // forever, since that effect simply never ran.
        if (userId != null) localStorage.setItem("userId", String(userId));
        return refreshRes;
      })
      .catch((e) => {
        localStorage.removeItem("token");
        localStorage.removeItem("userId");
        throw e;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

function installRefreshInterceptor(client) {
  client.interceptors.response.use(
    (res) => res,
    async (error) => {
      const req = error.config;

      if (req?.url?.includes("/refresh")) {
        return Promise.reject(error);
      }

      if (error.response?.status === 401 && req && !req._isRetry) {
        req._isRetry = true;
        try {
          await refreshOnce();
          return client.request(req);
        } catch (e) {
          return Promise.reject(error);
        }
      }

      return Promise.reject(error);
    },
  );
}

installRefreshInterceptor(authClient);
installRefreshInterceptor(apiClient);

